import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Archive } from 'lucide-react';
import { Role } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { RoleGate } from '@/auth/role-gate';
import { ConfirmDialog } from '@/components/confirm-dialog';
import {
  useArchiveContract,
  useContract,
  useUpdateContract,
} from '@/lib/contracts/use-contracts';
import { ContractForm } from './contract-form';

// /contracts/:contractUrn — edit + archive.
// Archive routes through the shared confirm dialog (TDS §9 irreversible-action UX).

export function ContractDetailRoute() {
  const { t } = useTranslation('network');
  const params = useParams<{ contractUrn: string }>();
  const contractUrn = params.contractUrn ? decodeURIComponent(params.contractUrn) : undefined;
  const contract = useContract(contractUrn);
  const update = useUpdateContract(contractUrn ?? '');
  const archive = useArchiveContract(contractUrn ?? '');

  if (contract.isLoading || !contractUrn) {
    return (
      <main className="density-page mx-auto max-w-3xl p-6">
        <Skeleton className="h-32 w-full" />
      </main>
    );
  }
  if (contract.isError || !contract.data) {
    return (
      <main className="density-page mx-auto max-w-3xl p-6">
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            {t('contracts.detail.loadError')}
          </CardContent>
        </Card>
      </main>
    );
  }

  const c = contract.data;
  const isArchived = c.status === 'inactive' || c.status === 'expired';

  return (
    <RoleGate min={Role.OPS_SUPERVISOR} hideWhenDenied>
      <main className="density-page mx-auto flex max-w-3xl flex-col gap-4 p-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{c.hotelProviderName}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('contracts.detail.summary', {
                airport: c.airportCode,
                status: t(`contracts.status.${c.status}`).toUpperCase(),
                currency: c.currency,
                rate: c.ratePerNight,
              })}
            </p>
          </div>
          {!isArchived ? (
            <ConfirmDialog
              title={t('contracts.detail.archive.title')}
              description={t('contracts.detail.archive.description')}
              confirmLabel={t('contracts.detail.archive.action')}
              confirmVariant="destructive"
              onConfirm={async () => {
                await archive.mutateAsync();
              }}
              trigger={
                <Button variant="outline" disabled={archive.isPending}>
                  <Archive className="mr-1 h-3 w-3" aria-hidden />
                  {archive.isPending
                    ? t('contracts.detail.archive.working')
                    : t('contracts.detail.archive.action')}
                </Button>
              }
            />
          ) : null}
        </header>

        <Card>
          <CardContent className="p-6">
            <ContractForm
              initial={{
                hotelProviderName: c.hotelProviderName,
                airportCode: c.airportCode,
                rateType: c.rateType,
                ratePerNight: c.ratePerNight,
                currency: c.currency,
                guaranteedRooms: c.guaranteedRooms,
                minStars: c.minStars,
                validFrom: c.validFrom,
                validUntil: c.validUntil,
                terms: c.terms ?? '',
              }}
              submitLabel={t('contracts.detail.save')}
              pending={update.isPending}
              onSubmit={async (input) => {
                await update.mutateAsync(input);
              }}
            />
          </CardContent>
        </Card>
      </main>
    </RoleGate>
  );
}
